import { api } from './api';
import s3Api from './s3Api';

// S3 presigned URL을 공개 URL로 변환
const convertPresignedUrlToPublicUrl = (presignedUrl) => {
  if (typeof presignedUrl !== 'string' || !presignedUrl.startsWith('http')) {
    console.error('Invalid presigned URL:', presignedUrl);
    return null;
  }

  try {
    const url = new URL(presignedUrl.trim());
    const hostParts = url.hostname.split('.');
    
    if (hostParts.length < 5) {
      console.error('Unexpected hostname format:', url.hostname);
      return null;
    }

    const bucketName = hostParts[0];
    const region = hostParts[2];
    const filePath = url.pathname.replace(/^\/+/, '');

    const fileUrl = `https://${bucketName}.s3.${region}.amazonaws.com/${filePath}`;
    return { fileUrl, bucketName, region, filePath };
  } catch (error) {
    console.error('Failed to parse URL:', error);
    return null;
  }
};

const cemeteryApi = {
  getAll: async (params) => {
    try {
      const res = await api.get('/cemeteries', { params });
      return res.data.data;
    } catch (error) {
      console.error('묘지 목록 조회 에러:', error);
      throw new Error(error.response?.data?.message || '묘지 목록을 불러오는데 실패했습니다.');
    }
  },
  getById: async (id) => {
    try {
      const res = await api.get(`/cemeteries/${id}`);
      return res.data.data;
    } catch (error) {
      console.error('묘지 상세 조회 에러:', error);
      throw new Error(error.response?.data?.message || '묘지 정보를 불러오는데 실패했습니다.');
    }
  },
  create: async (data) => {
    try {
      const res = await api.post('/cemeteries', data);
      return res.data.data;
    } catch (error) {
      console.error('묘지 생성 에러:', error);
      throw new Error(error.response?.data?.message || '묘지 생성에 실패했습니다.');
    }
  },
  update: async (id, data) => {
    try {
      const res = await api.put(`/cemeteries/${id}`, data);
      return res.data.data;
    } catch (error) {
      console.error('묘지 수정 에러:', error);
      throw new Error(error.response?.data?.message || '묘지 수정에 실패했습니다.');
    }
  },
  delete: async (id) => {
    try {
      const res = await api.delete(`/cemeteries/${id}`);
      return res.data;
    } catch (error) {
      console.error('묘지 삭제 에러:', error);
      throw new Error(error.response?.data?.message || '묘지 삭제에 실패했습니다.');
    }
  },
  toggleStatus: async (id) => {
    try {
      const res = await api.patch(`/cemeteries/${id}/toggle-status`);
      return res.data.data;
    } catch (error) {
      console.error('묘지 상태 변경 에러:', error);
      throw new Error(error.response?.data?.message || '묘지 상태 변경에 실패했습니다.');
    }
  },
  getActive: async () => {
    try {
      const res = await api.get('/cemeteries/active');
      return res.data.data;
    } catch (error) {
      console.error('활성 묘지 조회 에러:', error);
      throw new Error(error.response?.data?.message || '활성 묘지 목록을 불러오는데 실패했습니다.');
    }
  },
  getStatistics: async (id) => {
    try {
      const res = await api.get(`/cemeteries/${id}/statistics`);
      return res.data.data;
    } catch (error) {
      console.error('묘지 통계 조회 에러:', error);
      throw new Error(error.response?.data?.message || '묘지 통계를 불러오는데 실패했습니다.');
    }
  },

  // 묘지 프로필 이미지 관련 API
  getProfileImage: async (id) => {
    try {
      const res = await api.get(`/cemetery-map-info/${id}/profile-image`);
      return res.data.data;
    } catch (error) {
      console.error('프로필 이미지 조회 에러:', error);
      throw new Error(error.response?.data?.message || '프로필 이미지를 불러오는데 실패했습니다.');
    }
  },
  uploadProfileImage: async (id, file) => {
    try {
      console.log('=== 프로필 이미지 업로드 시작 ===');
      console.log('id:', id);
      console.log('file:', file);
      
      // 파일명 생성 (cemetery/{id}/profile_image/{timestamp}_{filename})
      const timestamp = Date.now();
      const fileName = `cemetery/${id}/profile_image/${timestamp}_${file.name}`;
      console.log('fileName:', fileName);
      
      // S3 업로드 URL 생성
      const uploadData = await s3Api.generateUploadUrl(fileName, file.type);
      console.log('uploadData:', uploadData);
      
      // S3에 파일 업로드 (fetch API 사용)
      const uploadResponse = await fetch(uploadData.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      console.log('uploadResponse.ok:', uploadResponse.ok);
      if (!uploadResponse.ok) {
        throw new Error('파일 업로드에 실패했습니다.');
      }

      // presigned URL을 공개 URL로 변환
      const urlData = convertPresignedUrlToPublicUrl(uploadData.uploadUrl);
      if (!urlData) {
        throw new Error('URL 변환에 실패했습니다.');
      }
      const fileUrl = urlData.fileUrl;
      console.log('fileUrl:', fileUrl);
      
      // 백엔드에 URL 저장
      console.log('백엔드에 URL 저장 요청:', { imageUrl: fileUrl });
      const res = await api.post(`/cemetery-map-info/${id}/profile-image`, {
        imageUrl: fileUrl
      });
      
      console.log('백엔드 응답:', res.data);
      return res.data.data;
    } catch (error) {
      console.error('프로필 이미지 업로드 에러:', error);
      throw new Error(error.response?.data?.message || '프로필 이미지 업로드에 실패했습니다.');
    }
  },
  deleteProfileImage: async (id) => {
    try {
      const res = await api.delete(`/cemetery-map-info/${id}/profile-image`);
      return res.data;
    } catch (error) {
      console.error('프로필 이미지 삭제 에러:', error);
      throw new Error(error.response?.data?.message || '프로필 이미지 삭제에 실패했습니다.');
    }
  },

  // 묘지 지도 정보 관련 API
  getMapInfo: async (id) => {
    try {
      const res = await api.get(`/cemetery-map-info/${id}/map-info`);
      return res.data.data;
    } catch (error) {
      console.error('지도 정보 조회 에러:', error);
      throw new Error(error.response?.data?.message || '지도 정보를 불러오는데 실패했습니다.');
    }
  },
  saveMapCorners: async (id, corners) => {
    try {
      const res = await api.post(`/cemetery-map-info/${id}/map-corners`, corners);
      return res.data.data;
    } catch (error) {
      console.error('지도 꼭짓점 저장 에러:', error);
      throw new Error(error.response?.data?.message || '지도 꼭짓점 저장에 실패했습니다.');
    }
  },
  saveMapTiles: async (id, tiles) => {
    try {
      const res = await api.post(`/cemetery-map-info/${id}/map-tiles`, tiles);
      return res.data.data;
    } catch (error) {
      console.error('지도 타일 정보 저장 에러:', error);
      throw new Error(error.response?.data?.message || '지도 타일 정보 저장에 실패했습니다.');
    }
  },

  // 묘지 기본 출발 위치 관련 API
  getDefaultStartingLocation: async (id) => {
    try {
      const res = await api.get(`/cemetery-map-info/${id}/default-starting-location`);
      return res.data.data;
    } catch (error) {
      console.error('기본 출발 위치 조회 에러:', error);
      throw new Error(error.response?.data?.message || '기본 출발 위치를 불러오는데 실패했습니다.');
    }
  },
  saveDefaultStartingLocation: async (id, location) => {
    try {
      const res = await api.post(`/cemetery-map-info/${id}/default-starting-location`, location);
      return res.data.data;
    } catch (error) {
      console.error('기본 출발 위치 저장 에러:', error);
      throw new Error(error.response?.data?.message || '기본 출발 위치 저장에 실패했습니다.');
    }
  },
  deleteDefaultStartingLocation: async (id) => {
    try {
      const res = await api.delete(`/cemetery-map-info/${id}/default-starting-location`);
      return res.data;
    } catch (error) {
      console.error('기본 출발 위치 삭제 에러:', error);
      throw new Error(error.response?.data?.message || '기본 출발 위치 삭제에 실패했습니다.');
    }
  },
};

export default cemeteryApi; 